import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Select from 'react-select';
import DashboardLayout from '../../components/DashboardLayout';
import axiosInstance from '../../utils/axiosInstance';
import AuthService from '../../services/authService';
import { getImageURL } from '../../utils/api';
import { groupStudentsByCourseAndBatch, formatScheduleType } from '../../utils/studentAttendanceGrouping';

const selectStyles = {
  control: (base, state) => ({
    ...base,
    borderColor: state.isFocused ? '#059669' : '#d1d5db',
    boxShadow: state.isFocused ? '0 0 0 2px rgba(5,150,105,0.2)' : 'none',
    '&:hover': { borderColor: '#059669' },
    borderRadius: '0.5rem', minHeight: '42px', fontSize: '0.875rem',
  }),
  placeholder: (base) => ({ ...base, color: '#9ca3af', fontSize: '0.875rem' }),
  menu: (base) => ({ ...base, borderRadius: '0.5rem', boxShadow: '0 10px 25px rgba(0,0,0,0.1)', border: '1px solid #e5e7eb', zIndex: 20 }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? '#d1fae5' : state.isFocused ? '#ecfdf5' : 'white',
    color: state.isSelected ? '#065f46' : '#1f2937', cursor: 'pointer',
  }),
  multiValue: (base) => ({ ...base, backgroundColor: '#d1fae5' }),
  multiValueLabel: (base) => ({ ...base, color: '#065f46' }),
};

function pctBadgeClass(pct) {
  if (pct === null || pct === undefined) return 'bg-gray-100 text-gray-500';
  if (pct >= 75) return 'bg-green-100 text-green-700';
  if (pct >= 50) return 'bg-amber-100 text-amber-700';
  return 'bg-red-100 text-red-700';
}

export default function StudentAttendanceReport() {
  const navigate = useNavigate();

  const [coordinateCourses, setCoordinateCourses] = useState({});
  const [accessibleCourseIds, setAccessibleCourseIds] = useState([]);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [accessLoaded, setAccessLoaded] = useState(false);

  const [batchOptions, setBatchOptions] = useState([]);
  const [batchOptionsLoading, setBatchOptionsLoading] = useState(true);
  const [selectedBatches, setSelectedBatches] = useState([]); // [] = all ongoing batches
  const [minPercentageInput, setMinPercentageInput] = useState('');
  const [appliedMinPercentage, setAppliedMinPercentage] = useState(null);
  const [search, setSearch] = useState('');

  const [students, setStudents] = useState([]);
  const [totalStudents, setTotalStudents] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // "Management" staff get the same unrestricted report access as Super Admin
  // (see RequireReportAccess in App.js) — without this, they'd otherwise be
  // narrowed down to their own (typically empty) coordinate_courses list.
  const isManagement = String(AuthService.getUser()?.superAdmin?.staff?.staffType || '').trim().toLowerCase() === 'management';

  const accessibleParam = useCallback(() => {
    if (isSuperAdmin || isManagement) return 'all';
    return (Array.isArray(accessibleCourseIds) ? accessibleCourseIds : [])
      .map((id) => Number(id))
      .filter((num) => Number.isFinite(num) && num > 0);
  }, [isSuperAdmin, isManagement, accessibleCourseIds]);

  useEffect(() => {
    let isMounted = true;
    (async () => {
      try {
        const accessRes = await axiosInstance.get('/admin/me/coordinate-courses').catch(() => ({ data: {} }));
        if (!isMounted) return;
        const data = accessRes.data || {};
        setCoordinateCourses(data.coordinateCourses || {});
        setAccessibleCourseIds(Array.isArray(data.accessibleCourseIds) ? data.accessibleCourseIds : []);
        setIsSuperAdmin(!!data.isSuperAdmin);
      } catch (e) {
        console.error('Failed to load coordinate courses', e);
      } finally {
        if (isMounted) setAccessLoaded(true);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, []);

  // Load batch options once access is known
  useEffect(() => {
    if (!accessLoaded) return;
    let isMounted = true;
    (async () => {
      setBatchOptionsLoading(true);
      try {
        const resp = await axiosInstance.get('/admin/dashboard/batches-with-students', {
          params: { accessibleCourses: accessibleParam() },
        });
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.batches) ? resp.data.batches : [];
        setBatchOptions(list);
      } catch (e) {
        console.error('Failed to load batch options', e);
      } finally {
        if (isMounted) setBatchOptionsLoading(false);
      }
    })();
    return () => {
      isMounted = false;
    };
  }, [accessLoaded, accessibleParam]);

  const groupedBatchOptions = useMemo(() => {
    const grouped = new Map();
    batchOptions.forEach((b) => {
      const courseKey = b.courseShortName || b.courseName || 'Other Courses';
      if (!grouped.has(courseKey)) grouped.set(courseKey, []);
      grouped.get(courseKey).push({
        value: b.batchId,
        label: `${b.batchInternalName || b.batchName}${b.whatsappGroupName ? ` – ${b.whatsappGroupName}` : ''} (${b.activeStudentCount})`,
      });
    });
    return Array.from(grouped.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([label, options]) => ({ label, options }));
  }, [batchOptions]);

  const loadReport = useCallback(async () => {
    if (!accessLoaded) return;
    setLoading(true);
    setError(null);
    try {
      const resp = await axiosInstance.get('/admin/dashboard/student-attendance', {
        params: {
          accessibleCourses: accessibleParam(),
          batchIds: selectedBatches.length > 0 ? selectedBatches.map((o) => o.value).join(',') : 'all',
          minPercentage: appliedMinPercentage !== null ? appliedMinPercentage : undefined,
        },
      });
      const data = resp?.data || {};
      setStudents(data.students || []);
      setTotalStudents(data.totalStudents || 0);
    } catch (err) {
      setError(err?.response?.data?.message || 'Failed to load student attendance report');
    } finally {
      setLoading(false);
    }
  }, [accessLoaded, accessibleParam, selectedBatches, appliedMinPercentage]);

  useEffect(() => {
    loadReport();
  }, [loadReport]);

  const applyMinPercentage = () => {
    const trimmed = minPercentageInput.trim();
    if (trimmed === '') {
      setAppliedMinPercentage(null);
      return;
    }
    const num = Number.parseFloat(trimmed);
    if (Number.isNaN(num) || num < 0 || num > 100) {
      setError('Minimum percentage must be a number between 0 and 100.');
      return;
    }
    setAppliedMinPercentage(num);
  };

  const clearMinPercentage = () => {
    setMinPercentageInput('');
    setAppliedMinPercentage(null);
  };

  const filteredStudents = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return students;
    return students.filter((s) => {
      const combined = [s.studentName, s.studentId, s.registrationNumber, s.batchInternalName, s.batchName, s.courseShortName, s.courseName]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return combined.includes(term);
    });
  }, [students, search]);

  // Group by course, then by batch — batches within a course ordered by
  // their overall attendance percentage, highest to lowest.
  const groupedByCourse = useMemo(() => groupStudentsByCourseAndBatch(filteredStudents), [filteredStudents]);

  const handlePrint = () => {
    const params = new URLSearchParams();
    params.set('batchIds', selectedBatches.length > 0 ? selectedBatches.map((o) => o.value).join(',') : 'all');
    if (appliedMinPercentage !== null) params.set('minPercentage', String(appliedMinPercentage));
    window.open(`/admin/report/student-attendance/print?${params.toString()}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <DashboardLayout title="Student Attendance Report">
      <div className="mx-auto max-w-7xl">
        <div className="mb-4 flex items-center justify-between">
          <button
            type="button"
            onClick={() => navigate('/admin/report/monitor')}
            className="inline-flex items-center gap-1.5 rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Monitor Report
          </button>
          <button
            type="button"
            onClick={handlePrint}
            className="inline-flex items-center gap-1.5 rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
          >
            <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4H7v4a2 2 0 002 2z" />
            </svg>
            Print Official Notice
          </button>
        </div>

        <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 p-6">
          <h2 className="flex items-center gap-2 text-base font-semibold text-emerald-800">
            <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            Student Attendance Report
          </h2>
          <p className="mt-1 text-sm text-emerald-900">
            Overall attendance percentage per student, aggregated across every subject in their batch(es).
            Leave batches unselected to include every ongoing batch.
          </p>

          <div className="mt-4 flex flex-wrap items-end gap-4">
            <div className="min-w-[280px] flex-1">
              <label className="mb-1 block text-xs font-medium text-gray-700">Batches</label>
              <Select
                isMulti
                isLoading={batchOptionsLoading}
                options={groupedBatchOptions}
                value={selectedBatches}
                onChange={(opts) => setSelectedBatches(opts || [])}
                placeholder="All ongoing batches (default)…"
                styles={selectStyles}
                closeMenuOnSelect={false}
              />
            </div>
            <div>
              <label className="mb-1 block text-xs font-medium text-gray-700">
                Minimum attendance % <span className="text-gray-400">(show students at or above this)</span>
              </label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min={0}
                  max={100}
                  step={1}
                  value={minPercentageInput}
                  onChange={(e) => setMinPercentageInput(e.target.value)}
                  placeholder="e.g. 80"
                  className="w-28 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
                />
                <button
                  type="button"
                  onClick={applyMinPercentage}
                  className="rounded-lg border border-emerald-600 bg-emerald-600 px-3 py-2 text-xs font-medium text-white hover:bg-emerald-700"
                >
                  Apply
                </button>
                {appliedMinPercentage !== null && (
                  <button
                    type="button"
                    onClick={clearMinPercentage}
                    className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-xs font-medium text-gray-600 hover:bg-gray-50"
                  >
                    Clear
                  </button>
                )}
              </div>
            </div>
            <div className="flex flex-col">
              <label className="mb-1 text-xs font-medium text-gray-700">Search</label>
              <input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Student / Batch / Course"
                className="w-56 rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-emerald-500 focus:outline-none"
              />
            </div>
          </div>

          {appliedMinPercentage !== null && (
            <p className="mt-3 text-xs font-medium text-emerald-800">
              Showing only students at or above {appliedMinPercentage}% attendance.
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-800">{error}</div>
        )}

        <div className="mb-3 flex items-center justify-between">
          <h3 className="text-sm font-semibold text-gray-700">
            {totalStudents} student{totalStudents === 1 ? '' : 's'}
          </h3>
        </div>

        {loading ? (
          <div className="rounded-xl border border-gray-200 bg-white p-8 text-center text-gray-600">
            Loading student attendance…
          </div>
        ) : groupedByCourse.length === 0 ? (
          <div className="rounded-xl border border-gray-200 bg-gray-50 p-8 text-center text-gray-600">
            No students match the current filters.
          </div>
        ) : (
          <div className="space-y-10">
            {groupedByCourse.map((course) => (
              <div key={course.courseId ?? course.courseName}>
                <h3 className="mb-3 text-lg font-bold text-gray-900">
                  {course.courseShortName || course.courseName || 'Unknown Course'}
                  {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
                </h3>

                <div className="space-y-5">
                  {course.batches.map((batch) => (
                    <div key={batch.batchId} className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-sm">
                      <div className="flex items-center justify-between gap-3 border-b border-gray-100 bg-gray-50 px-5 py-3">
                        <div className="min-w-0">
                          <p className="truncate text-sm font-semibold text-gray-800">
                            {batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch'}
                          </p>
                          <p className="text-xs text-gray-500">
                            {batch.students.length} student{batch.students.length === 1 ? '' : 's'}
                          </p>
                        </div>
                        <span className={`shrink-0 rounded-full px-3 py-1 text-xs font-bold ${pctBadgeClass(batch.avgPercentage)}`}>
                          {batch.avgPercentage === null ? '—' : `${batch.avgPercentage}%`} avg
                        </span>
                      </div>

                      <div className="overflow-x-auto">
                        <table className="w-full min-w-[900px] text-left">
                          <thead>
                            <tr className="border-b border-gray-100 text-[10px] font-semibold uppercase tracking-wide text-gray-400">
                              <th className="px-5 py-2.5">Student</th>
                              <th className="px-5 py-2.5">Student ID</th>
                              <th className="px-5 py-2.5">Registration No.</th>
                              <th className="px-5 py-2.5 text-center">Classes Held</th>
                              <th className="px-5 py-2.5 text-center">Attended</th>
                              <th className="px-5 py-2.5 text-center">Absent</th>
                              <th className="px-5 py-2.5 text-center">Attendance %</th>
                            </tr>
                          </thead>
                          <tbody className="divide-y divide-gray-100">
                            {batch.students.map((s) => (
                              <tr key={`${s.studentId}-${s.registrationNumber}-${s.batchId}`} className="hover:bg-gray-50">
                                <td className="px-5 py-3">
                                  <div className="flex items-center gap-3">
                                    <img
                                      src={getImageURL(s.imagePath)}
                                      alt={s.studentName}
                                      className="h-9 w-9 rounded-full object-cover ring-1 ring-gray-200"
                                    />
                                    <span className="max-w-[180px] truncate text-sm font-medium text-gray-800">{s.studentName || '—'}</span>
                                  </div>
                                </td>
                                <td className="px-5 py-3 text-xs text-gray-600">{s.studentId || '—'}</td>
                                <td className="px-5 py-3 text-xs text-gray-600">{s.registrationNumber || '—'}</td>
                                <td className="px-5 py-3 text-center text-xs text-gray-600">{s.totalClasses}</td>
                                <td className="px-5 py-3 text-center text-xs text-gray-600">{s.attendedClasses}</td>
                                <td className="px-5 py-3 text-center text-xs text-gray-600">{s.absentClasses}</td>
                                <td className="px-5 py-3 text-center">
                                  <span className={`inline-block rounded-full px-2 py-1 text-xs font-bold ${pctBadgeClass(s.overallPercentage)}`}>
                                    {s.overallPercentage === null ? '—' : `${s.overallPercentage}%`}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
